
import React, { useState } from "react";
import axios from "axios";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import {
    AlertCircle,
    ArrowRight,
    CheckCircle2,
    FileText,
    Mail,
    MessageSquare,
    Phone,
    ShieldCheck,
    User,
} from "lucide-react";

const API_URL = "https://dubaibudgetivehotels.com/api";

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));

        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !form.name.trim() ||
            !form.email.trim() ||
            !form.phone.trim() ||
            !form.message.trim()
        ) {
            setError("Please fill in all required fields.");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            if (!executeRecaptcha) {
                setError(
                    "Security verification is not ready. Please refresh the page and try again."
                );
                return;
            }

            const token = await executeRecaptcha("contact_form");

            const res = await axios.post(`${API_URL}/contact`, {
                ...form,
                recaptcha_token: token,
            });

            if (res.data.success) {
                setSuccess(
                    "Your message has been sent successfully. Our team will get back to you shortly."
                );

                setForm({
                    name: "",
                    email: "",
                    phone: "",
                    subject: "",
                    message: "",
                });
            } else {
                setError(
                    res.data.message || "Failed to send your message."
                );
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const inputClass =
        "w-full rounded-xl border border-[#DCEAE1] bg-[#F8FBF9] py-4 pl-12 pr-4 text-[14px] font-medium text-[#17324D] placeholder:text-[#9AA8B5] outline-none transition duration-200 focus:border-[#168B43] focus:bg-white focus:ring-4 focus:ring-[#168B43]/10";

    const labelClass =
        "mb-2.5 block text-[13px] font-extrabold text-[#17324D]";

    const iconClass =
        "pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#168B43]";

    return (
        <div className="w-full">
            <div className="relative overflow-hidden rounded-[22px] border border-[#DCEAE1] bg-white shadow-[0_18px_55px_rgba(6,78,59,0.06)]">

                {/* TOP ACCENT */}
                <div className="h-1.5 bg-gradient-to-r from-[#064E3B] via-[#168B43] to-[#A5E8B8]" />

                <div className="relative p-6 sm:p-9 lg:p-11">

                    {/* FORM HEADING */}
                    <div className="mb-9">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#CBE8D5] bg-[#E8F7EE] px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
                            <span className="h-2 w-2 rounded-full bg-[#168B43]" />
                            Contact Form
                        </div>

                        <h3 className="mt-5 text-[29px] font-extrabold tracking-tight text-[#17324D] sm:text-[36px]">
                            Send Us A{" "}
                            <span className="text-[#168B43]">
                                Message.
                            </span>
                        </h3>

                        <p className="mt-4 max-w-[630px] text-[14px] leading-8 text-[#718096]">
                            Complete the form below and tell us how we can
                            assist you. Our team will get back to you as soon
                            as possible.
                        </p>
                    </div>

                    {/* SUCCESS MESSAGE */}
                    {success && (
                        <div
                            role="status"
                            className="mb-7 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-5"
                        >
                            <CheckCircle2
                                size={22}
                                className="mt-0.5 shrink-0 text-emerald-600"
                            />

                            <div>
                                <p className="text-sm font-extrabold text-emerald-800">
                                    Message Sent Successfully
                                </p>

                                <p className="mt-2 text-sm leading-7 text-emerald-700">
                                    {success}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ERROR MESSAGE */}
                    {error && (
                        <div
                            role="alert"
                            className="mb-7 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-5"
                        >
                            <AlertCircle
                                size={22}
                                className="mt-0.5 shrink-0 text-rose-600"
                            />

                            <div>
                                <p className="text-sm font-extrabold text-rose-800">
                                    Unable To Send Message
                                </p>

                                <p className="mt-2 text-sm leading-7 text-rose-700">
                                    {error}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* FORM */}
                    <form onSubmit={handleSubmit} className="space-y-6">

                        {/* NAME + EMAIL */}
                        <div className="grid gap-6 md:grid-cols-2">

                            <div>
                                <label
                                    htmlFor="contact-name"
                                    className={labelClass}
                                >
                                    Full Name
                                    <span className="ml-1 text-[#168B43]">*</span>
                                </label>

                                <div className="relative">
                                    <User size={18} className={iconClass} />

                                    <input
                                        id="contact-name"
                                        type="text"
                                        name="name"
                                        autoComplete="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="Enter your full name"
                                        className={inputClass}
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="contact-email"
                                    className={labelClass}
                                >
                                    Email Address
                                    <span className="ml-1 text-[#168B43]">*</span>
                                </label>

                                <div className="relative">
                                    <Mail size={18} className={iconClass} />

                                    <input
                                        id="contact-email"
                                        type="email"
                                        name="email"
                                        autoComplete="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        placeholder="you@example.com"
                                        className={inputClass}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* PHONE + SUBJECT */}
                        <div className="grid gap-6 md:grid-cols-2">

                            <div>
                                <label
                                    htmlFor="contact-phone"
                                    className={labelClass}
                                >
                                    Phone Number
                                    <span className="ml-1 text-[#168B43]">*</span>
                                </label>

                                <div className="relative">
                                    <Phone size={18} className={iconClass} />

                                    <input
                                        id="contact-phone"
                                        type="tel"
                                        name="phone"
                                        autoComplete="tel"
                                        value={form.phone}
                                        onChange={handleChange}
                                        placeholder="+971 50 123 4567"
                                        className={inputClass}
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="contact-subject"
                                    className={labelClass}
                                >
                                    Subject
                                    <span className="ml-2 font-medium text-[#9AA8B5]">
                                        (Optional)
                                    </span>
                                </label>

                                <div className="relative">
                                    <FileText size={18} className={iconClass} />

                                    <input
                                        id="contact-subject"
                                        type="text"
                                        name="subject"
                                        value={form.subject}
                                        onChange={handleChange}
                                        placeholder="What is your enquiry about?"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* MESSAGE */}
                        <div>
                            <label
                                htmlFor="contact-message"
                                className={labelClass}
                            >
                                Your Message
                                <span className="ml-1 text-[#168B43]">*</span>
                            </label>

                            <div className="relative">
                                <MessageSquare
                                    size={18}
                                    className="pointer-events-none absolute left-4 top-4 text-[#168B43]"
                                />

                                <textarea
                                    id="contact-message"
                                    name="message"
                                    rows={6}
                                    value={form.message}
                                    onChange={handleChange}
                                    placeholder="Tell us about your travel plans, booking enquiry, or how we can help..."
                                    className={`${inputClass} resize-y`}
                                    required
                                />
                            </div>
                        </div>

                        {/* FORM FOOTER */}
                        <div className="flex flex-col justify-between gap-6 border-t border-[#E3ECE6] pt-7 sm:flex-row sm:items-center">

                            <div className="flex items-start gap-3">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                    <ShieldCheck size={22} />
                                </div>

                                <div>
                                    <p className="text-[13px] font-extrabold text-[#17324D]">
                                        Secure Enquiry
                                    </p>

                                    <p className="mt-1 max-w-[270px] text-xs leading-6 text-[#718096]">
                                        Protected by Google reCAPTCHA verification.
                                    </p>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="group inline-flex min-h-[54px] w-full items-center justify-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white shadow-lg shadow-[#168B43]/15 transition duration-300 hover:-translate-y-0.5 hover:bg-[#087D37] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {loading ? (
                                    <>
                                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        Send Message
                                        <ArrowRight
                                            size={18}
                                            className="transition-transform group-hover:translate-x-1"
                                        />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
